/**
 * The Keycloak singleton, configured per node.
 *
 * One web deployment can serve many nodes (backend docs/node-pooling.md), and
 * each node may sign in against its own realm and public client. So the
 * instance is created lazily: AuthContext first asks the backend's /info for
 * the node's `auth` block and calls configureKeycloak() with it; anything that
 * reads `keycloak` before that gets an instance built from the build-time /
 * runtime env (window._env_), which is still right for a single-node deploy.
 *
 * The default export behaves like the Keycloak instance itself (token,
 * tokenParsed, init(), updateToken(), login(), ...) so existing call sites
 * are unchanged; it just forwards to whichever instance is current.
 */
import Keycloak from 'keycloak-js';
import getEnv from './env';

let instance = null;
let configured = false;
// The node's company identity provider (/info auth.idp_hint): every login()
// goes straight to it unless the caller names another.
let idpHint = null;

const envConfig = () => ({
  url: getEnv('REACT_APP_KEYCLOAK_URL') || 'http://localhost:8080',
  realm: getEnv('REACT_APP_KEYCLOAK_REALM') || 'vellby',
  clientId: getEnv('REACT_APP_KEYCLOAK_CLIENT_ID') || 'vellby-web-app',
});

/**
 * Apply the node's auth block from GET /info: { url, realm, client_id, idp_hint }.
 * Missing or null fields keep the env value. Must run before keycloak.init();
 * a second call after init is ignored (the instance is live by then).
 */
export function configureKeycloak(auth) {
  if (configured) return getKeycloak();
  idpHint = (auth && auth.idp_hint) || null;
  const base = envConfig();
  instance = new Keycloak({
    url: (auth && auth.url) || base.url,
    realm: (auth && auth.realm) || base.realm,
    clientId: (auth && auth.client_id) || base.clientId,
  });
  configured = true;
  return instance;
}

export function getKeycloak() {
  if (!instance) instance = new Keycloak(envConfig());
  return instance;
}

/** Marks the instance as initialised so configureKeycloak() stops swapping it. */
export function lockKeycloak() {
  configured = true;
}

const keycloak = new Proxy({}, {
  get(_target, prop) {
    const kc = getKeycloak();
    if (prop === 'login' && idpHint) {
      return (options) => kc.login({ idpHint, ...options });
    }
    const value = kc[prop];
    return typeof value === 'function' ? value.bind(kc) : value;
  },
  set(_target, prop, value) {
    getKeycloak()[prop] = value;
    return true;
  },
});

export default keycloak;
